import React, { FC, useRef } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { OverviewReleases } from './OverviewReleases';
import { useInjectedAnchors } from '../../hooks/useInjectedAnchors';
import styled from '@mui/styles/styled';
import { ShowMoreState, useShowMore } from '../../hooks/useShowMore';
import { ButtonWithChevronWithLink } from '../ButtonWithChevron/ButtonWithChevron';
import { useTranslation } from 'react-i18next';
import { Changelog } from '../Changelog/Changelog';
import { ISummaryLatest } from '../../resources/api/apiOverview';
import { IProductDetails } from '../../resources/models/IProduct';
import { AuthenticatedRoutes } from '../../routers/AuthenticatedRoutes';

interface IProps {
  xs?: number;
  lg?: number;
  product: Omit<IProductDetails, 'plans'>;
  latest: ISummaryLatest;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  height: '100%',
  padding: `${theme.spacing(2)} 11%`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
}));

const TableContainer = styled('div')({
  maxHeight: 300,
  overflowY: 'auto',
});

const ReleaseNotes = styled(Changelog)(({ theme }) => ({
  '&.collapsed': {
    maxHeight: 200,
    overflow: 'hidden',
    position: 'relative',
    '&::after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 50,
      background: `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
    },
  },
}));

export const ProductOverviewReleases: FC<IProps> = ({ product, latest, xs, lg }) => {
  const { t } = useTranslation();

  const latestVersion = latest.version;
  const latestInstaller = latest.installer[0];

  return latestInstaller && latestVersion && latest.installer.length > 0 ? (
    <Grid item xs={xs} lg={lg}>
      <StyledPaper>
        <div>
          <Typography variant="h4" textAlign="center">
            {latestInstaller.version}
          </Typography>
          <Typography variant="subtitle1" textAlign="center" marginBottom={2}>
            {t('userOverview.productOverview.latestRelease')}
          </Typography>

          <TableContainer>
            <OverviewReleases product={product} installers={latest.installer} />
          </TableContainer>
        </div>

        <ButtonWithChevronWithLink to={AuthenticatedRoutes.UserDownloadsProduct(product.id)}>
          {t('userOverview.productOverview.goToDownloads')}
        </ButtonWithChevronWithLink>
      </StyledPaper>
    </Grid>
  ) : null;
};

export const ProductOverviewLatestVersion: FC<IProps> = ({ product, latest, xs, lg }) => {
  const { t } = useTranslation();
  const changelogRef = useRef<HTMLDivElement>(null);
  const dialogChangelogRef = useRef<HTMLDivElement>(null);
  useInjectedAnchors(changelogRef);
  const { showMore, setShowMore, setHideMore } = useShowMore(changelogRef, 200);

  const latestVersion = latest.version;
  const latestInstaller = latest.installer[0];

  return latestVersion && latestInstaller ? (
    <Grid item xs={xs} lg={lg}>
      <StyledPaper>
        <div>
          <Typography variant="h4" textAlign="center">
            {t('userOverview.productOverview.title')}
          </Typography>
          <Typography variant="subtitle1" textAlign="center" marginBottom={2}>
            {t('userOverview.productOverview.releaseVersion', { version: latestVersion })}
          </Typography>

          <ReleaseNotes
            dangerouslySetInnerHTML={{ __html: latestInstaller.changelog }}
            ref={changelogRef}
            className={showMore !== ShowMoreState.INITIAL ? 'collapsed' : void 0}
          />

          {showMore !== ShowMoreState.INITIAL ? <Button onClick={setShowMore}>{t('common.readMore')}</Button> : null}

          {latestInstaller ? (
            <Dialog open={showMore === ShowMoreState.VISIBLE} onClose={setHideMore} maxWidth="md" fullWidth>
              <DialogTitle>
                {product.name} {latestVersion}
              </DialogTitle>
              <DialogContent>
                <ReleaseNotes
                  dangerouslySetInnerHTML={{ __html: latestInstaller.changelog }}
                  ref={dialogChangelogRef}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={setHideMore}>{t('common.dialog.close')}</Button>
              </DialogActions>
            </Dialog>
          ) : null}
        </div>

        <ButtonWithChevronWithLink to={AuthenticatedRoutes.UserDownloadsProduct(product.id)}>
          {t('userOverview.productOverview.goToDownloads')}
        </ButtonWithChevronWithLink>
      </StyledPaper>
    </Grid>
  ) : null;
};
